import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import SlackStyleInput from '@/components/SlackStyleInput';
import MessageRenderer from '@/components/MessageRenderer';
import { 
  Send, 
  Hash, 
  Users, 
  MessageSquare, 
  Building2,
  Reply,
  CheckCircle,
  Clock,
  ArrowRight,
  Heart,
  MessageCircle,
  MoreHorizontal,
  Plus,
  ChevronDown,
  ClipboardList,
  Filter,
  User,
  X,
  Calendar,
  Crown,
  MapPin
} from 'lucide-react';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { makeAuthenticatedRequest } from '@/lib/auth';
import { useToast } from '@/hooks/use-toast';
import { formatDateDisplay } from '@/lib/dateUtils';

interface Team {
  id: number;
  name: string;
  description?: string;
  isPrivate: boolean;
  createdById: number;
  createdAt: string;
  updatedAt: string;
}

interface Channel {
  id: number;
  name: string;
  teamId: number;
  type: 'channel' | 'dm';
  description?: string;
  isPrivate: boolean;
  createdById: number;
  createdAt: string;
  updatedAt: string;
}

interface Message {
  id: number;
  content: string;
  authorId: number;
  authorName?: string;
  authorRole?: string;
  channelId: number;
  teamId: number;
  messageType: string;
  createdAt: string;
  // Enhanced inbox fields
  teamName?: string;
  channelName?: string;
  isAnswered?: boolean;
  priority?: string;
  assignedTo?: number;
  isPinned?: boolean;
}

interface Reply {
  id: number;
  content: string;
  authorId: number;
  authorName?: string;
  authorRole?: string;
  messageId: number;
  createdAt: string;
}

interface FileAttachment {
  id: number;
  fileName: string;
  fileSize: number;
  fileType: string;
  wasabiUrl: string;
  uploadedBy: number;
  uploadedByName?: string;
  uploadedAt: string;
}

interface Task {
  id: number;
  title: string;
  description?: string;
  status: 'todo' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  dueDate?: string;
  createdById: number;
  assignedTo?: number;
  teamId?: number;
  weddingId?: number;
  createdAt: string;
  updatedAt: string;
  assigneeName?: string;
  createdByName?: string;
  teamName?: string;
  assignedToName?: string;
  // Enhanced wedding context fields from Phase 2
  clientName?: string;
  eventDate?: string;
  eventSlot?: string;
  weddingTitle?: string;
}

interface UserProfile {
  id: number;
  username: string;
  email: string;
  role: string;
  firstName: string;
  lastName: string;
  phone?: string;
}

// Task Management View Component - Moved outside to fix React hooks issue
function TaskManagementView({ userProfile, onViewChange }: { userProfile: UserProfile; onViewChange: (view: string) => void }) {
  const [selectedFilter, setSelectedFilter] = useState('open');
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState<{ taskId: number; newStatus: string; currentTitle: string } | null>(null);
  const [statusComment, setStatusComment] = useState('');
  const [newComments, setNewComments] = useState<{[taskId: number]: string}>({});
  const { toast } = useToast();

  // Fetch client tickets for client users (Zoho support tickets)
  const { data: clientSupportTickets = [], isLoading: isLoadingClientTickets, error: clientTicketsError } = useQuery({
    queryKey: ['/api/support/tickets'],
    enabled: userProfile.role === 'client'
  });

  // Client view lists support tickets only. (It used to also request
  // /api/client/comm-hub-tasks, a route that does not exist — a 404 on every visit.)
  const clientTasks = useMemo(() => {
    if (userProfile.role !== 'client' || !Array.isArray(clientSupportTickets)) return [];
    return clientSupportTickets
      .map((ticket: any) => ({ ...ticket, ticketType: 'support_ticket', source: 'Zoho Support' }))
      .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [clientSupportTickets, userProfile.role]);

  const isLoadingClientTasks = isLoadingClientTickets;
  const clientTasksError = clientTicketsError;

  // For clients, show support tickets interface instead of task management
  if (userProfile.role === 'client') {
    return (
      <div className="flex flex-col h-full">
        {/* Client Tickets Header */}
        <div className="p-4 border-b border-slate-700 bg-slate-800">
          <div className="flex items-center gap-2">
            <Building2 className="w-6 h-6 text-amber-500" />
            <h1 className="text-xl font-bold text-white">Your Support Tickets</h1>
            <p className="text-sm text-slate-400 ml-2">
              Event managers will create tickets for you as needed
            </p>
          </div>
        </div>



        {/* Client Tickets List */}
        <div className="flex-1 p-4">
          {isLoadingClientTasks ? (
            <div className="text-center text-slate-500 py-8">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50 animate-pulse" />
              <h3 className="text-xl font-semibold mb-2">Loading Your Tickets...</h3>
            </div>
          ) : clientTasksError ? (
            <div className="text-center text-slate-400 py-8" role="alert">
              <h3 className="text-xl font-semibold mb-2 text-white">We couldn't load your tickets</h3>
              <p>Please check your connection and refresh the page.</p>
            </div>
          ) : clientTasks.length === 0 ? (
            <div className="text-center text-slate-500 py-8">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-semibold mb-2">No Support Tickets</h3>
              <p className="text-slate-400">Your event manager will create support tickets for you when needed.</p>
              <p className="text-slate-400 mt-2">Check back here to view ticket updates and status.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {clientTasks.map((ticket: any) => (
                <div
                  key={ticket.id}
                  className="bg-slate-800 border border-slate-700 rounded-lg p-4 hover:border-slate-600 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-1">{ticket.subject}</h3>
                      {ticket.description && (
                        <p className="text-slate-300 text-sm mb-2">{ticket.description}</p>
                      )}
                    </div>
                    <div className="ml-4 text-right">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                        ticket.status === 'Closed' ? 'bg-green-100 text-green-800' :
                        ticket.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                        ticket.status === 'Open' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {ticket.status || 'Open'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm text-slate-400">
                    <div className="flex items-center gap-4">
                      <span className={`px-2 py-1 rounded text-xs ${
                        ticket.priority === 'Urgent' ? 'bg-red-100 text-red-700' :
                        ticket.priority === 'High' ? 'bg-orange-100 text-orange-700' :
                        ticket.priority === 'Medium' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {ticket.priority || 'Medium'} priority
                      </span>
                      {ticket.category && (
                        <span className="text-slate-500">{ticket.category}</span>
                      )}
                    </div>
                    
                    <div className="text-right">
                      {ticket.zohoAssigneeId && (
                        <div className="text-slate-400">Ticket: #{ticket.zohoTicketNumber || ticket.id}</div>
                      )}
                      <div className="text-slate-500">
                        {formatDateDisplay(ticket.createdAt)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Fetch tasks (for admin/event_manager users - all tasks)
  const { data: tasks = [], isLoading: isLoadingTasks, refetch: refetchTasks } = useQuery({
    queryKey: ['/api/comm-hub/tasks'],
    enabled: userProfile.role === 'admin' || userProfile.role === 'event_manager',
  });



  // Filter tasks based on selected filter (support desk style)
  const filteredTasks = useMemo(() => {
    if (!Array.isArray(tasks)) return [];
    
    switch (selectedFilter) {
      case 'open':
        return tasks.filter((task: Task) => task.status === 'pending');
      case 'pending':
        return tasks.filter((task: Task) => task.status === 'pending' && task.priority === 'high');
      case 'progress':
        return tasks.filter((task: Task) => task.status === 'in_progress');
      case 'closed':
        return tasks.filter((task: Task) => task.status === 'completed' || task.status === 'cancelled');
      case 'all':
      default:
        return tasks;
    }
  }, [tasks, selectedFilter]);

  // Fetch users for assignment dropdown (only event managers for task assignment)
  const { data: users = [] } = useQuery({
    queryKey: ['/api/admin/users', { for_task_assignment: 'true' }],
    enabled: userProfile.role === 'admin' || userProfile.role === 'event_manager',
  });

  // Task status update mutation
  const updateTaskStatusMutation = useMutation({
    mutationFn: async ({ taskId, status, comment }: { taskId: number; status: string; comment?: string }) => {
      return await apiRequest(`/api/comm-hub/tasks/${taskId}/status`, {
        method: 'PUT',
        data: { status, comment }
      });
    },
    onSuccess: () => {
      refetchTasks();
      setShowStatusModal(null);
      setStatusComment('');
      toast({ title: "Task status updated successfully" });
    },
    onError: () => {
      toast({ title: "Failed to update task status", variant: "destructive" });
    }
  });

  // Task assignment mutation
  const assignTaskMutation = useMutation({
    mutationFn: async ({ taskId, assignedTo }: { taskId: number; assignedTo: number | null }) => {
      return await apiRequest(`/api/comm-hub/tasks/${taskId}`, {
        method: 'PATCH',
        data: { assignedTo }
      });
    },
    onSuccess: () => {
      refetchTasks();
      toast({ title: "Task assignment updated successfully" });
    },
    onError: () => {
      toast({ title: "Failed to update task assignment", variant: "destructive" });
    }
  });

  // Comments are always visible now - no toggle needed

  // Add task comment mutation
  const addCommentMutation = useMutation({
    mutationFn: async ({ taskId, content }: { taskId: number; content: string }) => {
      return await apiRequest(`/api/comm-hub/tasks/${taskId}/comments`, {
        method: 'POST',
        data: { content }
      });
    },
    onSuccess: (_, { taskId }) => {
      setNewComments(prev => ({ ...prev, [taskId]: '' }));
      toast({ title: "Comment added successfully" });
      // Refetch comments using correct query key
      queryClient.invalidateQueries({ queryKey: [`/api/comm-hub/tasks/${taskId}/comments`] });
    },
    onError: () => {
      toast({ title: "Failed to add comment", variant: "destructive" });
    }
  });

  // Support ticket creation mutation
  const createTicketMutation = useMutation({
    mutationFn: async (ticketData: { subject: string; priority: string; category: string; description: string }) => {
      console.log('🎫 Creating support ticket:', ticketData);
      const response = await fetch('/api/support/tickets', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...ticketData,
          weddingId: null // Optional field as per API
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to create ticket' }));
        throw new Error(errorData.message || 'Failed to create ticket');
      }

      return response.json();
    },
    onSuccess: (result) => {
      console.log('✅ Ticket created successfully:', result);
      toast({
        title: "Support Ticket Created",
        description: "Your support ticket has been submitted successfully",
      });
      setShowCreateTask(false);
      // Ticket form uses native HTML FormData, no state to reset
      // Refresh support tickets to show the new ticket
      queryClient.invalidateQueries({ queryKey: ['/api/support/tickets'] });
    },
    onError: (error: any) => {
      console.error('❌ Failed to create ticket:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create support ticket",
        variant: "destructive",
      });
    },
  });

  // Handle status change with comment modal for completion/cancellation
  const handleStatusChange = (taskId: number, newStatus: string, currentTitle: string) => {
    if (newStatus === 'completed' || newStatus === 'cancelled') {
      setShowStatusModal({ taskId, newStatus, currentTitle });
    } else {
      updateTaskStatusMutation.mutate({ taskId, status: newStatus });
    }
  };

  // Submit status update with comment
  const submitStatusUpdate = () => {
    if (showStatusModal) {
      updateTaskStatusMutation.mutate({ 
        taskId: showStatusModal.taskId, 
        status: showStatusModal.newStatus,
        comment: statusComment.trim() || undefined
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-gray-500';
      case 'in_progress': return 'bg-blue-500';
      case 'completed': return 'bg-green-500';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return 'border-l-gray-400';
      case 'normal': return 'border-l-blue-400';
      case 'high': return 'border-l-orange-400';
      case 'urgent': return 'border-l-red-400';
      default: return 'border-l-gray-400';
    }
  };

  // Create task mutation - MOVED BEFORE CONDITIONAL RETURN
  const createTaskMutation = useMutation({
    mutationFn: async (taskData: any) => {
      console.log('📤 Sending task creation request:', taskData);
      try {
        const response = await apiRequest('/api/comm-hub/tasks', {
          method: 'POST',
          data: taskData
        });
        
        console.log('📡 Response status:', response.status);
        console.log('📡 Response ok:', response.ok);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.log('❌ Server error response:', errorText);
          throw new Error(`Server error ${response.status}: ${errorText}`);
        }
        
        const jsonData = await response.json();
        console.log('✅ Task creation successful:', jsonData);
        return jsonData;
      } catch (error) {
        console.error('🚨 Task creation mutation error:', error);
        console.error('🚨 Error type:', typeof error);
        console.error('🚨 Error message:', error instanceof Error ? error.message : 'Unknown error');
        console.error('🚨 Full error object:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      console.log('🎉 Task created successfully:', data);
      refetchTasks();
      setShowCreateTask(false);
      toast({ title: "Task created successfully" });
    },
    onError: (error) => {
      console.error('🚨 Task creation mutation error:', error);
      toast({ title: "Failed to create task", variant: "destructive" });
    }
  });

  if (isLoadingTasks) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center text-slate-400">
          <ClipboardList className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>Loading tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col">
      {/* Task View Header with Navigation */}
      <div className="p-4 border-b border-slate-700 bg-slate-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="w-6 h-6 text-amber-500" />
            <h1 className="text-xl font-bold text-white">
              {userProfile.role === 'client' ? 'Support Tickets' : 'Task Management'}
            </h1>
          </div>
          
          {/* Navigation buttons - Mobile optimized */}
          <div className="flex gap-1 sm:gap-2 bg-slate-700 rounded-lg p-1">
            {(userProfile.role === 'admin' || userProfile.role === 'event_manager') && (
              <button
                onClick={() => onViewChange('inbox')}
                className="px-2 sm:px-3 py-2 rounded-md text-xs sm:text-sm font-medium text-slate-300 hover:text-white hover:bg-slate-600 transition-colors mobile-touch-optimize"
              >
                Inbox
              </button>
            )}
            <button
              onClick={() => onViewChange('teams')}
              className="px-2 sm:px-3 py-2 rounded-md text-xs sm:text-sm font-medium text-slate-300 hover:text-white hover:bg-slate-600 transition-colors mobile-touch-optimize"
            >
              Teams
            </button>
            <button
              onClick={() => onViewChange('tasks')}
              className="px-2 sm:px-3 py-2 rounded-md text-xs sm:text-sm font-medium bg-amber-600 text-white transition-colors mobile-touch-optimize"
            >
              {userProfile.role === 'client' ? 'Tickets' : 'Tasks'}
            </button>
          </div>
        </div>
      </div>

      {/* Task Creation Modal */}
      {showCreateTask && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Create New Task</h3>
                <button
                  onClick={() => setShowCreateTask(false)}
                  className="text-slate-400 hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target as HTMLFormElement);
                const taskData = {
                  title: formData.get('title'),
                  description: formData.get('description'),
                  priority: formData.get('priority'),
                  category: formData.get('category'),
                  teamId: parseInt(formData.get('teamId') as string),
                  dueDate: formData.get('dueDate') || null
                };
                createTaskMutation.mutate(taskData);
              }} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Title *
                  </label>
                  <input
                    name="title"
                    type="text"
                    required
                    className="w-full h-10 px-3 py-2 bg-slate-700 text-white border border-slate-600 rounded-md focus:border-amber-500 focus:outline-none"
                    placeholder="Enter task title"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    rows={3}
                    className="w-full px-3 py-2 bg-slate-700 text-white border border-slate-600 rounded-md focus:border-amber-500 focus:outline-none resize-none"
                    placeholder="Enter task description"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Priority
                    </label>
                    <select
                      name="priority"
                      className="w-full h-10 px-3 py-2 bg-slate-700 text-white border border-slate-600 rounded-md focus:border-amber-500 focus:outline-none"
                      defaultValue="medium"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Category
                    </label>
                    <select
                      name="category"
                      className="w-full h-10 px-3 py-2 bg-slate-700 text-white border border-slate-600 rounded-md focus:border-amber-500 focus:outline-none"
                    >
                      <option value="general" selected>General</option>
                      <option value="decoration">Decoration</option>
                      <option value="catering">Catering</option>
                      <option value="coordination">Coordination</option>
                      <option value="vendor">Vendor</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Team *
                  </label>
                  <select
                    name="teamId"
                    required
                    className="w-full h-10 px-3 py-2 bg-slate-700 text-white border border-slate-600 rounded-md focus:border-amber-500 focus:outline-none"
                  >
                    <option value="">Select a team</option>
                    <option value="1">17-Sep-Day</option>
                    <option value="2">Team 2</option>
                    <option value="3">Team 3</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Due Date
                  </label>
                  <input
                    name="dueDate"
                    type="datetime-local"
                    className="w-full h-10 px-3 py-2 bg-slate-700 text-white border border-slate-600 rounded-md focus:border-amber-500 focus:outline-none"
                  />
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateTask(false)}
                    className="px-4 py-2 text-slate-300 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createTaskMutation.isPending}
                    className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-md transition-colors disabled:opacity-50"
                  >
                    {createTaskMutation.isPending ? 'Creating...' : 'Create Task'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Task Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">
                  {showStatusModal.newStatus === 'completed' ? 'Complete Task' : 'Cancel Task'}
                </h3>
                <button
                  onClick={() => setShowStatusModal(null)}
                  className="text-slate-400 hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="mb-4">
                <p className="text-slate-300 text-sm mb-2">Task: {showStatusModal.currentTitle}</p>
                <p className="text-slate-400 text-sm">
                  {showStatusModal.newStatus === 'completed' 
                    ? 'Add a completion note (optional):' 
                    : 'Add a cancellation reason (optional):'}
                </p>
              </div>
              
              <textarea
                value={statusComment}
                onChange={(e) => setStatusComment(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 bg-slate-700 text-white border border-slate-600 rounded-md focus:border-amber-500 focus:outline-none resize-none"
                placeholder={showStatusModal.newStatus === 'completed' 
                  ? "Describe what was completed or any final notes..." 
                  : "Explain why this task was cancelled..."}
              />
              
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowStatusModal(null)}
                  className="px-4 py-2 text-slate-300 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={submitStatusUpdate}
                  disabled={updateTaskStatusMutation.isPending}
                  className={`px-4 py-2 text-white rounded-md transition-colors ${
                    showStatusModal.newStatus === 'completed'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  } ${updateTaskStatusMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {updateTaskStatusMutation.isPending 
                    ? 'Updating...' 
                    : showStatusModal.newStatus === 'completed' 
                      ? 'Mark Complete' 
                      : 'Mark Cancelled'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="p-4 border-b border-slate-700 bg-slate-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ClipboardList className="w-6 h-6 text-amber-400" />
            <h2 className="text-xl font-semibold text-white">Task Management</h2>
            <Badge variant="outline" className="border-slate-600 text-slate-400">
              {filteredTasks.length} tasks
            </Badge>
          </div>
          <Button
            onClick={() => setShowCreateTask(true)}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Task
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="p-4 border-b border-slate-700 bg-slate-900/50">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-medium text-slate-300">Filter by:</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'open', label: 'Open', count: tasks.filter((t: Task) => t.status === 'pending').length },
            { key: 'pending', label: 'Pending', count: tasks.filter((t: Task) => t.status === 'pending' && t.priority === 'high').length },
            { key: 'progress', label: 'In Progress', count: tasks.filter((t: Task) => t.status === 'in_progress').length },
            { key: 'closed', label: 'Closed', count: tasks.filter((t: Task) => t.status === 'completed' || t.status === 'cancelled').length },
            { key: 'all', label: 'All Tasks', count: tasks.length }
          ].map((filter) => (
            <button
              key={filter.key}
              onClick={() => setSelectedFilter(filter.key)}
              className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedFilter === filter.key
                  ? 'bg-amber-600 text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              {filter.label}
              <Badge variant="secondary" className="ml-1 bg-slate-800/80 text-slate-300 text-xs">
                {filter.count}
              </Badge>
            </button>
          ))}
        </div>
      </div>

      {/* Task List */}
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full p-4">
          {filteredTasks.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardList className="w-16 h-16 mx-auto mb-4 text-slate-500 opacity-50" />
              <h3 className="text-lg font-semibold text-slate-400 mb-2">No tasks found</h3>
              <p className="text-slate-500">
                {selectedFilter === 'all' 
                  ? 'Create your first task to get started.'
                  : `No tasks match the selected filter: ${selectedFilter.replace('-', ' ')}`
                }
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredTasks.map((task: Task) => (
                <Card key={task.id} className={`bg-slate-800/50 border-slate-700 border-l-4 ${getPriorityColor(task.priority)} hover:bg-slate-800/70 transition-colors`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-white truncate">{task.title}</h3>
                          <Badge className={`text-xs text-white ${getStatusColor(task.status)}`}>
                            {task.status.replace('_', ' ')}
                          </Badge>
                          <Badge variant="outline" className={`text-xs ${
                            task.priority === 'urgent' ? 'border-red-500 text-red-400' :
                            task.priority === 'high' ? 'border-orange-500 text-orange-400' :
                            task.priority === 'normal' ? 'border-blue-500 text-blue-400' :
                            'border-gray-500 text-gray-400'
                          }`}>
                            {task.priority}
                          </Badge>
                        </div>
                        
                        {/* Client and Team Information */}
                        <div className="flex items-center gap-4 mb-2 text-xs text-slate-400">
                          {task.teamName && (
                            <div className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              <span>{task.teamName}</span>
                            </div>
                          )}
                          {task.createdByName && (
                            <div className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              <span>Created by: {task.createdByName}</span>
                            </div>
                          )}
                          {task.assignedToName && (
                            <div className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              <span>Assigned to: {task.assignedToName}</span>
                            </div>
                          )}
                        </div>

                        {/* Enhanced Wedding Context Information */}
                        {(task.clientName || task.eventDate || task.eventSlot || task.weddingTitle) && (
                          <div className="flex items-center gap-4 mb-2 text-xs">
                            {task.clientName && (
                              <div className="flex items-center gap-1 text-amber-400">
                                <Heart className="w-3 h-3" />
                                <span className="font-medium">{task.clientName}</span>
                              </div>
                            )}
                            {task.eventDate && (
                              <div className="flex items-center gap-1 text-emerald-400">
                                <Calendar className="w-3 h-3" />
                                <span>{task.eventDate}</span>
                              </div>
                            )}
                            {task.eventSlot && (
                              <div className="flex items-center gap-1 text-blue-400">
                                <Clock className="w-3 h-3" />
                                <span>{task.eventSlot}</span>
                              </div>
                            )}
                            {task.weddingTitle && (
                              <div className="flex items-center gap-1 text-purple-400">
                                <Crown className="w-3 h-3" />
                                <span className="italic">{task.weddingTitle}</span>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {task.description && (
                          <p className="text-sm text-slate-300 mb-3 line-clamp-2">
                            {task.description}
                          </p>
                        )}
                        
                        <div className="flex items-center gap-4 text-xs text-slate-400">
                          {task.assigneeName && (
                            <div className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              <span>Assigned: {task.assigneeName}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            <span>Created: {format(new Date(task.createdAt), 'MMM d, h:mm a')}</span>
                          </div>
                          {task.dueDate && (
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              <span>Due: {format(new Date(task.dueDate), 'MMM d, h:mm a')}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 ml-4">
                        {/* Assignment Dropdown */}
                        {task.status !== 'completed' && task.status !== 'cancelled' && (
                          <select
                            value={task.assignedTo || ''}
                            onChange={(e) => {
                              const assignedTo = e.target.value ? parseInt(e.target.value) : null;
                              assignTaskMutation.mutate({ taskId: task.id, assignedTo });
                            }}
                            className="w-40 h-8 px-2 py-1 bg-slate-700 text-white border border-slate-600 rounded-md text-xs focus:border-amber-500 focus:outline-none"
                            disabled={assignTaskMutation.isPending}
                          >
                            <option value="">Unassigned</option>
                            {Array.isArray(users) && users.map((user: any) => (
                              <option key={user.id} value={user.id}>
                                {user.firstName} {user.lastName} ({user.role})
                              </option>
                            ))}
                          </select>
                        )}
                        
                        {/* Status Dropdown */}
                        {task.status !== 'completed' && task.status !== 'cancelled' && (
                          <select
                            value={task.status}
                            onChange={(e) => handleStatusChange(task.id, e.target.value, task.title)}
                            className="w-32 h-8 px-2 py-1 bg-slate-700 text-white border border-slate-600 rounded-md text-xs focus:border-amber-500 focus:outline-none"
                            disabled={updateTaskStatusMutation.isPending}
                          >
                            <option value="pending">Pending</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        )}
                        
                        {/* Comments Toggle Button */}
                        {/* Comments always visible - toggle button removed */}
                      </div>
                    </div>
                  </CardContent>
                  
                  {/* Inline Comments Section - Always Visible */}
                  <TaskComments 
                    taskId={task.id} 
                    userProfile={userProfile} 
                    newComments={newComments}
                    setNewComments={setNewComments}
                    addCommentMutation={addCommentMutation}
                  />
                </Card>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>


    </div>
  );
}

// TaskComments component to render inline comments
const TaskComments = ({ taskId, userProfile, newComments, setNewComments, addCommentMutation }: { 
  taskId: number; 
  userProfile: UserProfile; 
  newComments: Record<number, string>;
  setNewComments: React.Dispatch<React.SetStateAction<Record<number, string>>>;
  addCommentMutation: any;
}) => {
  const { data: comments = [] } = useQuery({
    queryKey: [`/api/comm-hub/tasks/${taskId}/comments`],
  });

  const handleAddComment = (e: React.FormEvent) => {
    e.preventDefault();
    const content = newComments[taskId]?.trim();
    if (content) {
      addCommentMutation.mutate({ taskId, content });
    }
  };

  return (
    <div className="border-t border-slate-600 bg-slate-800/50 p-4">
      <h4 className="text-sm font-medium text-amber-400 mb-3">Comments</h4>
      
      {/* Comments List */}
      <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
        {comments.length === 0 ? (
          <p className="text-slate-400 text-sm">No comments yet</p>
        ) : (
          comments.map((comment: any) => (
            <div key={comment.id} className="bg-slate-700 rounded-lg p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-amber-400 font-medium text-sm">
                  {comment.authorName || `User ${comment.authorId}`}
                </span>
                <span className="text-slate-400 text-xs">
                  {new Date(comment.createdAt).toLocaleDateString()}
                </span>
              </div>
              <p className="text-slate-200 text-sm">{comment.content}</p>
            </div>
          ))
        )}
      </div>

      {/* Add Comment Form */}
      <form onSubmit={handleAddComment} className="flex gap-2">
        <input
          type="text"
          value={newComments[taskId] || ''}
          onChange={(e) => setNewComments(prev => ({ ...prev, [taskId]: e.target.value }))}
          placeholder="Add a comment..."
          className="flex-1 h-9 px-3 py-2 bg-slate-700 text-white border border-slate-600 rounded-md text-sm focus:border-amber-500 focus:outline-none"
        />
        <button
          type="submit"
          disabled={!newComments[taskId]?.trim() || addCommentMutation.isPending}
          className="h-9 px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-600/50 text-white text-sm rounded-md transition-colors"
        >
          {addCommentMutation.isPending ? 'Adding...' : 'Add'}
        </button>
      </form>
    </div>
  );
};

export default function CommHubFixed() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Get user profile first - using authenticated request
  const { data: userProfile, isLoading: profileLoading } = useQuery({
    queryKey: ['/api/user/profile'],
    queryFn: async () => {
      console.log("🔍 DEBUG: Fetching user profile from /api/user/profile");
      const response = await makeAuthenticatedRequest('/api/user/profile');
      console.log("🔍 DEBUG: Profile response status:", response.status);
      if (!response.ok) {
        console.error("🔍 DEBUG: Profile fetch failed:", response.status);
        throw new Error('Failed to fetch user profile');
      }
      const data = await response.json();
      console.log("🔍 DEBUG: Profile data received:", data);
      console.log("🔍 DEBUG: User role from profile:", data.role);
      console.log("🔍 DEBUG: User ID from profile:", data.id);
      return data;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 3
  });

  // Get URL search params to check for view parameter
  const urlParams = new URLSearchParams(window.location.search);
  const viewParam = urlParams.get('view');
  
  // Determine initial view based on user role and URL params
  const getInitialView = () => {
    // If view param is provided, use it
    if (viewParam === 'inbox') return 'inbox';
    if (viewParam === 'teams') return 'teams';
    
    // Default based on user role
    if (userProfile?.role === 'admin' || userProfile?.role === 'event_manager') {
      return 'inbox';
    }
    return 'teams';
  };

  const [activeView, setActiveView] = useState<'teams' | 'inbox' | 'tasks'>('teams');
  const [selectedTeam, setSelectedTeam] = useState<number | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<number | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [replyMode, setReplyMode] = useState<{ [key: number]: boolean }>({});
  const [replyContent, setReplyContent] = useState<{ [key: number]: string }>({});
  const [currentFilter, setCurrentFilter] = useState<'unanswered' | 'all' | 'priority' | 'assigned' | 'following_up' | 'in_progress'>('unanswered');

  // Auto-select first team when teams load
  const [hasAutoSelectedTeam, setHasAutoSelectedTeam] = useState(false);

  // Check if user can access inbox (admin/event_manager only)
  const canAccessInbox = userProfile?.role === 'admin' || userProfile?.role === 'event_manager';

  // Set view based on user role and URL params when userProfile loads
  useEffect(() => {
    if (userProfile) {
      console.log('📋 Setting view for user:', userProfile.role, 'canAccessInbox:', canAccessInbox);
      const urlParams = new URLSearchParams(window.location.search);
      const viewParam = urlParams.get('view');
      
      // Handle view setting with proper URL parameter support for all views
      if (userProfile.role === 'admin' || userProfile.role === 'event_manager') {
        if (viewParam === 'teams') {
          console.log('📋 Setting view to teams via URL param for admin');
          setActiveView('teams');
        } else if (viewParam === 'tasks') {
          console.log('📋 Setting view to tasks via URL param for admin');
          setActiveView('tasks');
        } else {
          console.log('📋 Setting default view to inbox for admin/event_manager');
          setActiveView('inbox');
        }
      } else {
        // Client users default to teams unless specifically requesting inbox or tasks
        if (viewParam === 'inbox') {
          console.log('📋 Setting view to inbox via URL param for client');
          setActiveView('inbox');
        } else if (viewParam === 'tasks') {
          console.log('📋 Setting view to tasks via URL param for client');
          setActiveView('tasks');
        } else {
          console.log('📋 Setting view to teams for client');
          setActiveView('teams');
        }
      }
    }
  }, [userProfile, canAccessInbox]);

  // Auto-set default view for clients to 'teams'
  useEffect(() => {
    if (userProfile && !canAccessInbox && activeView === 'inbox') {
      setActiveView('teams');
    }
  }, [userProfile, canAccessInbox, activeView]);

  // Optimized queries with reduced polling
  const { data: teamsData } = useQuery<Team[]>({
    queryKey: ['/api/comm-hub/teams'],
    refetchInterval: 10000,
    staleTime: 5000,
  });

  const teams = Array.isArray(teamsData) ? teamsData : [];

  // Auto-select behavior based on user role
  useEffect(() => {
    if (teams.length > 0 && !hasAutoSelectedTeam && userProfile) {
      if (userProfile.role === 'client') {
        // Client: Start with teams view so they can access the tabbed interface
        const clientTeam = teams[0]; // Client only has one team
        console.log('👤 Client auto-selecting team and staying in teams view:', clientTeam.id);
        setSelectedTeam(clientTeam.id);
        setActiveView('teams'); // Keep clients in teams view to show sidebar
        setHasAutoSelectedTeam(true);
      } else if (userProfile.role === 'admin' || userProfile.role === 'event_manager') {
        // Admin/Event Manager: Start with inbox view
        console.log('🔄 Auto-selecting first team for admin/event_manager:', teams[0].id);
        setSelectedTeam(teams[0].id);
        setHasAutoSelectedTeam(true);
      }
    }
  }, [teams, selectedTeam, hasAutoSelectedTeam, userProfile]);

  // Load channels for all teams
  const { data: allChannelsData } = useQuery<Channel[]>({
    queryKey: ['/api/comm-hub/all-channels'],
    queryFn: async () => {
      const allChannels: Channel[] = [];
      // Load channels for each team
      for (const team of teams) {
        try {
          const teamChannels = await (await apiRequest(`/api/comm-hub/teams/${team.id}/channels`)).json();
          if (Array.isArray(teamChannels)) {
            allChannels.push(...teamChannels);
          }
        } catch (error) {
          console.error(`Failed to load channels for team ${team.id}:`, error);
        }
      }
      return allChannels;
    },
    enabled: teams.length > 0,
    refetchInterval: 15000,
    staleTime: 10000,
  });

  const channels = Array.isArray(allChannelsData) ? allChannelsData : [];

  // Mobile-specific: Auto-select first channel when team is selected and on mobile
  useEffect(() => {
    const isMobile = window.innerWidth < 768; // Only treat truly small screens as mobile
    
    if (isMobile && selectedTeam && !selectedChannel && channels && channels.length > 0) {
      const teamChannels = channels.filter((channel: Channel) => channel.teamId === selectedTeam);
      if (teamChannels.length > 0) {
        // Prioritize "General" channel, then first available
        const generalChannel = teamChannels.find(c => c.name.toLowerCase() === 'general');
        const channelToSelect = generalChannel || teamChannels[0];
        console.log("🔍 DEBUG: Mobile auto-selecting channel:", channelToSelect);
        setSelectedChannel(channelToSelect.id);
      }
    }
  }, [selectedTeam, channels, selectedChannel]);

  // Also load channels for the currently selected team for the channel view
  const { data: selectedTeamChannelsData, isLoading: channelsLoading, error: channelsError } = useQuery<Channel[]>({
    queryKey: [`/api/comm-hub/teams/${selectedTeam}/channels`],
    enabled: !!selectedTeam,
    refetchInterval: 10000,
    staleTime: 5000,
  });

  const selectedTeamChannels = Array.isArray(selectedTeamChannelsData) ? selectedTeamChannelsData : [];

  // Auto-select #general channel for clients when channels load
  useEffect(() => {
    if (selectedTeamChannels.length > 0 && !selectedChannel && userProfile?.role === 'client') {
      // Find the general channel first, otherwise use the first available channel
      const generalChannel = selectedTeamChannels.find(channel => channel.name === 'general');
      const targetChannel = generalChannel || selectedTeamChannels[0];
      console.log('👤 Client auto-selecting channel:', targetChannel.name, targetChannel.id);
      setSelectedChannel(targetChannel.id);
    }
  }, [selectedTeamChannels, selectedChannel, userProfile?.role]);

  const { data: messagesData } = useQuery<Message[]>({
    queryKey: ['/api/comm-hub/channels', selectedTeam, selectedChannel, 'messages'],
    queryFn: async () => {
      if (!selectedChannel || !selectedTeam) return [];
      const response = await apiRequest(`/api/comm-hub/teams/${selectedTeam}/channels/${selectedChannel}/messages`);
      const result = await response.json();
      return result;
    },
    enabled: !!selectedChannel,
    refetchInterval: 15000,
    staleTime: 10000,
  });

  const { data: inboxMessagesData } = useQuery<Message[]>({
    queryKey: ['/api/comm-hub/inbox'],
    refetchInterval: 15000,
    staleTime: 10000,
  });

  // Replies and attachments are embedded in messages, not separate endpoints
  // Disabled these queries to prevent 404 errors
  // const { data: repliesData } = useQuery<Reply[]>({
  //   queryKey: ['/api/comm-hub/replies'],
  //   refetchInterval: 15000,
  //   staleTime: 10000,
  // });

  // const { data: attachmentsData } = useQuery<FileAttachment[]>({
  //   queryKey: ['/api/comm-hub/attachments'],
  //   refetchInterval: 30000,
  //   staleTime: 20000,
  // });

  const messages = Array.isArray(messagesData) ? messagesData : [];
  const inboxMessages = Array.isArray(inboxMessagesData) ? inboxMessagesData : [];
  const replies: Reply[] = [];
  const attachments: FileAttachment[] = [];

  // Filter inbox messages based on current filter
  const filteredInboxMessages = useMemo(() => {
    if (!Array.isArray(inboxMessages)) return [];
    
    switch (currentFilter) {
      case 'unanswered':
        return inboxMessages.filter((msg: Message) => !msg.isAnswered && msg.status !== 'closed');
      case 'all':
        return inboxMessages;
      case 'priority':
        return inboxMessages.filter((msg: Message) => msg.priority === 'high');
      case 'assigned':
        return inboxMessages.filter((msg: Message) => msg.assignedTo === userProfile?.id);
      case 'following_up':
        return inboxMessages.filter((msg: Message) => msg.status === 'following_up');
      case 'in_progress':
        return inboxMessages.filter((msg: Message) => msg.status === 'in_progress');
      case 'closed':
        return inboxMessages.filter((msg: Message) => msg.status === 'closed');
      default:
        return inboxMessages;
    }
  }, [inboxMessages, currentFilter, userProfile?.id]);

  // Calculate filter counts
  const filterCounts = useMemo(() => {
    if (!Array.isArray(inboxMessages)) return { unanswered: 0, all: 0, priority: 0, assigned: 0, following_up: 0, in_progress: 0, closed: 0 };
    
    return {
      unanswered: inboxMessages.filter((msg: Message) => !msg.isAnswered && msg.status !== 'closed').length,
      all: inboxMessages.length,
      priority: inboxMessages.filter((msg: Message) => msg.priority === 'high').length,
      assigned: inboxMessages.filter((msg: Message) => msg.assignedTo === userProfile?.id).length,
      following_up: inboxMessages.filter((msg: Message) => msg.status === 'following_up').length,
      in_progress: inboxMessages.filter((msg: Message) => msg.status === 'in_progress').length,
      closed: inboxMessages.filter((msg: Message) => msg.status === 'closed').length,
    };
  }, [inboxMessages, userProfile?.id]);

  // Mutations
  const sendMessageMutation = useMutation({
    mutationFn: (data: any) => {
      const { teamId, channelId, ...messageData } = data;
      return apiRequest(`/api/comm-hub/teams/${teamId}/channels/${channelId}/messages`, {
        method: 'POST',
        data: messageData
      });
    },
    onSuccess: () => {
      // Invalidate with exact query key match
      queryClient.invalidateQueries({ queryKey: ['/api/comm-hub/channels', selectedTeam, selectedChannel, 'messages'] });
      queryClient.invalidateQueries({ queryKey: ['/api/comm-hub/inbox'] });
      // Force immediate refetch
      queryClient.refetchQueries({ queryKey: ['/api/comm-hub/channels', selectedTeam, selectedChannel, 'messages'] });
      setNewMessage('');
    },
  });

  const sendReplyMutation = useMutation({
    mutationFn: async (data: { content: string; messageId: number; channelId: number; teamId: number }) => {
      return apiRequest(`/api/comm-hub/teams/${data.teamId}/channels/${data.channelId}/messages`, {
        method: 'POST',
        data: {
          content: data.content,
          messageType: 'message',
          threadId: data.messageId, // This creates the reply thread
        }
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/comm-hub/messages'] });
      queryClient.invalidateQueries({ queryKey: ['/api/comm-hub/inbox'] });
      queryClient.invalidateQueries({ queryKey: ['/api/comm-hub/channels', variables.teamId, variables.channelId, 'messages'] });
      setReplyContent(prev => ({ ...prev, [variables.messageId]: '' }));
      setReplyMode(prev => ({ ...prev, [variables.messageId]: false }));
    },
  });

  const updateMessageStatusMutation = useMutation({
    mutationFn: (data: { messageId: number; isAnswered?: boolean; assignedTo?: number; status?: string }) => {
      console.log(`🔄 Sending status update request:`, data);
      return apiRequest(`/api/comm-hub/messages/${data.messageId}`, {
        method: 'PATCH',
        data: { isAnswered: data.isAnswered, assignedTo: data.assignedTo, status: data.status }
      });
    },
    onSuccess: (result, variables) => {
      console.log(`✅ Status update successful for message ${variables.messageId}`);
      queryClient.invalidateQueries({ queryKey: ['/api/comm-hub/channels', selectedTeam, selectedChannel, 'messages'] });
      queryClient.invalidateQueries({ queryKey: ['/api/comm-hub/inbox'] });
      
      let statusText = 'Status updated';
      if (variables.status === 'done' || variables.isAnswered) statusText = 'Message marked as Done';
      else if (variables.status === 'following_up') statusText = 'Following up on message';
      else if (variables.status === 'in_progress') statusText = 'Message marked as In Progress';
      
      toast({
        title: "Status Updated",
        description: statusText,
      });
    },
    onError: (error, variables) => {
      console.error(`❌ Status update failed for message ${variables.messageId}:`, error);
      toast({
        title: "Update Failed",
        description: "Failed to update message status",
        variant: "destructive",
      });
    }
  });

  const uploadFilesMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      console.log('📤 Starting file upload mutation...');
      
      const response = await fetch('/api/comm-hub/upload', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      console.log('📤 Upload response status:', response.status);
      console.log('📤 Upload response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        let errorDetails;
        try {
          errorDetails = await response.json();
        } catch {
          errorDetails = { message: await response.text() };
        }
        
        console.error('❌ Upload failed with details:', errorDetails);
        
        const errorMessage = errorDetails.error || errorDetails.message || `HTTP ${response.status}`;
        throw new Error(`Upload failed: ${errorMessage}`);
      }
      
      const result = await response.json();
      console.log('✅ Upload successful:', result);
      return result;
    },
    onSuccess: () => {
      console.log('✅ Upload mutation success - invalidating cache');
      // Attachments endpoint removed - attachments are embedded in messages
      // queryClient.invalidateQueries({ queryKey: ['/api/comm-hub/attachments'] });
    },
    onError: (error) => {
      console.error('❌ Upload mutation error:', error);
    }
  });

  const handleSendMessage = async (content: string, files?: File[]) => {
    if (!content.trim() && !files?.length) return;
    
    if (!selectedChannel) {
      toast({
        title: "No Channel Selected",
        description: "Please select a channel to send your message",
        variant: "destructive",
      });
      return;
    }

    console.log('📤 Sending message with files:', { content, files: files?.length || 0 });

    try {
      let attachmentUrls: string[] = [];

      // If files exist, upload them first
      if (files && files.length > 0) {
        console.log('📎 Uploading files...');
        const formData = new FormData();
        files.forEach((file, index) => {
          formData.append(`files`, file);
        });

        try {
          const uploadResult = await uploadFilesMutation.mutateAsync(formData);
          console.log('✅ Files uploaded:', uploadResult);
          
          // Extract attachment URLs from upload result
          if (uploadResult && uploadResult.files) {
            attachmentUrls = uploadResult.files.map((file: any) => file.url);
          }
        } catch (uploadError) {
          console.error('❌ File upload failed:', uploadError);
          toast({
            title: "File Upload Failed",
            description: "Files couldn't be uploaded, sending message without attachments",
            variant: "destructive",
          });
        }
      }

      // Send message with or without attachments
      const messageData = {
        content: content.trim(),
        channelId: selectedChannel,
        teamId: selectedTeam,
        messageType: files && files.length > 0 ? 'file' : 'message',
        attachments: attachmentUrls.length > 0 ? attachmentUrls : null
      };

      console.log('📤 Final message data:', messageData);
      const result = await sendMessageMutation.mutateAsync(messageData);
      console.log('✅ Message sent successfully:', result);
      
      toast({
        title: "Message Sent",
        description: files && files.length > 0 
          ? `Message sent with ${files.length} attachment(s)` 
          : "Your message has been sent successfully",
      });
    } catch (error) {
      console.error('❌ Message send failed:', error);
      toast({
        title: "Error",
        description: "Failed to send message",
        variant: "destructive",
      });
    }
  };

  const handleSendReply = async (messageId: number, originalMessage?: any) => {
    const content = replyContent[messageId]?.trim();
    if (!content) return;

    // For inbox replies, use the original message's channel and team
    // For channel replies, use the selected channel and team
    const channelId = originalMessage?.channelId || selectedChannel;
    const teamId = originalMessage?.teamId || selectedTeam;

    console.log('🔄 Sending reply:', { messageId, content, channelId, teamId });

    const replyData = {
      content,
      messageId,
      channelId,
      teamId,
    };

    try {
      await sendReplyMutation.mutateAsync(replyData);
      toast({
        title: "Reply Sent",
        description: "Your reply has been sent successfully",
      });
    } catch (error) {
      console.error('❌ Reply send failed:', error);
      toast({
        title: "Error",
        description: "Failed to send reply",
        variant: "destructive",
      });
    }
  };

  // Convert message to task mutation
  const convertToTaskMutation = useMutation({
    mutationFn: async (taskData: any) => {
      console.log('📤 Sending task creation request:', taskData);
      try {
        const response = await apiRequest('/api/comm-hub/tasks', {
          method: 'POST',
          data: taskData
        });
        // apiRequest already handles JSON parsing and throws on errors
        // We just need to parse the response as JSON since apiRequest returns the raw Response
        const jsonData = await response.json();
        console.log('✅ Task creation successful:', jsonData);
        return jsonData;
      } catch (error) {
        console.error('❌ Task creation failed:', error);
        
        // Enhanced error logging to capture the full error details
        if (error instanceof Error) {
          console.error('❌ Error message:', error.message);
          console.error('❌ Error stack:', error.stack);
        }
        
        // Log the full error object for debugging
        console.error('❌ Full error object:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      console.log('🎉 Task created successfully:', data);
      toast({
        title: "Task Created",
        description: "Message has been converted to a task successfully. Click the Tasks tab to view it.",
      });
      // Don't auto-switch to tasks view to avoid blank page issue
      // User can manually click Tasks tab to see the new task
    },
    onError: (error: any) => {
      console.error('💥 Task creation error:', error);
      const errorMessage = error?.message || error?.response?.data?.message || 'Failed to convert message to task';
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  });

  const handleConvertToTask = (message: Message) => {
    const taskData = {
      title: message.content.length > 50 ? 
        message.content.substring(0, 50) + '...' : 
        message.content || `Task from ${message.authorName}`,
      description: `Converted from message: "${message.content}"\n\nOriginal message by: ${message.authorName} (${message.authorRole})\nSent: ${format(new Date(message.createdAt), 'MMM d, yyyy h:mm a')}\nTeam: ${message.teamName}\nChannel: #${message.channelName}`,
      status: 'pending', // Must match database constraint: 'pending', 'in_progress', 'completed', 'cancelled'
      priority: message.priority === 'high' || message.priority === 'urgent' ? 'high' : 'medium',
      teamId: message.teamId,
      channelId: message.channelId,
      messageId: message.id, // Link to original message
      category: 'general',
      isVisible: true,
      assignedTo: null, // Will be assigned manually later
      assignedBy: userProfile?.id, // Current user creating the task
    };

    console.log('🔄 Converting message to task:', taskData);
    console.log('🔄 User profile for assignedBy:', userProfile);
    convertToTaskMutation.mutate(taskData);
  };

  const addReactionMutation = useMutation({
    mutationFn: async ({ messageId, emoji }: { messageId: number; emoji: string }) => {
      console.log(`👍 Adding reaction ${emoji} to message ${messageId}`);
      const response = await apiRequest(`/api/comm-hub/messages/${messageId}/reactions`, {
        method: 'POST',
        data: { emoji }
      });
      return response;
    },
    onSuccess: () => {
      // Invalidate and refetch messages to show updated reactions
      queryClient.invalidateQueries({ queryKey: ['/api/comm-hub/channels'] });
      queryClient.invalidateQueries({ queryKey: ['/api/comm-hub/inbox'] });
    },
    onError: (error) => {
      console.error('❌ Reaction failed:', error);
      toast({
        title: "Error",
        description: "Failed to add reaction",
        variant: "destructive",
      });
    }
  });

  const handleReaction = (messageId: number, reaction: string) => {
    console.log(`🔄 Adding reaction ${reaction} to message ${messageId}`);
    addReactionMutation.mutate({ messageId, emoji: reaction });
    toast({
      title: "Reaction Added",
      description: `${reaction} reaction added to message`,
    });
  };

  // Handler for message action buttons
  const handleMessageAction = async (messageId: number, action: string) => {
    const currentUserId = userProfile?.id;
    console.log(`🔄 Processing message action: ${action} for message ${messageId} by user ${currentUserId}`);
    
    try {
      switch (action) {
        case 'done':
          await updateMessageStatusMutation.mutateAsync({
            messageId,
            isAnswered: true,
            status: 'closed',
            assignedTo: currentUserId
          });
          console.log(`✅ Message ${messageId} marked as closed`);
          toast({
            title: "Message closed",
            description: "The message has been moved to closed status.",
          });
          break;
        case 'following_up':
          await updateMessageStatusMutation.mutateAsync({
            messageId,
            status: 'following_up',
            assignedTo: currentUserId
          });
          console.log(`📋 Message ${messageId} marked as following up`);
          toast({
            title: "Following up",
            description: "The message has been marked for follow-up.",
          });
          break;
        case 'in_progress':
          await updateMessageStatusMutation.mutateAsync({
            messageId,
            status: 'in_progress',
            assignedTo: currentUserId
          });
          console.log(`⚡ Message ${messageId} marked as in progress`);
          toast({
            title: "In progress",
            description: "The message has been marked as in progress.",
          });
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(`❌ Message action failed:`, error);
      toast({
        title: "Action Failed",
        description: "Failed to update message status",
        variant: "destructive",
      });
    }
  }

  const toggleReply = (messageId: number) => {
    setReplyMode(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  const handleReplyContentChange = (messageId: number, content: string) => {
    setReplyContent(prev => ({
      ...prev,
      [messageId]: content
    }));
  };

  const handleReplyKeyPress = (messageId: number, e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendReply(messageId);
    }
  };



  const SlackStyleInboxMessage = ({ message }: { message: Message }) => {
    const messageReplies = replies.filter(reply => reply.messageId === message.id);
    
    return (
      <div className="group relative bg-slate-800/50 hover:bg-slate-800/70 rounded-lg p-4 transition-colors">
        <div className="flex items-start gap-3">
          <Avatar className="w-8 h-8 border-2 border-amber-500/20">
            <AvatarFallback className="bg-amber-500/20 text-amber-300 text-sm">
              {message.authorName?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-white">{message.authorName || 'Unknown User'}</span>
              <span className="text-xs text-slate-400">{message.authorRole}</span>
              <span className="text-xs text-slate-500">
                {format(new Date(message.createdAt), 'MMM d, h:mm a')}
              </span>
              
              {message.teamName && (
                <Badge variant="outline" className="text-xs border-slate-600 text-slate-300">
                  {message.teamName}
                </Badge>
              )}
              
              {message.channelName && (
                <Badge variant="outline" className="text-xs border-slate-600 text-slate-300">
                  #{message.channelName}
                </Badge>
              )}
              
              {message.priority && (
                <Badge 
                  variant="outline" 
                  className={`text-xs ${
                    message.priority === 'high' || message.priority === 'urgent' 
                      ? 'border-red-500 text-red-400' 
                      : 'border-yellow-500 text-yellow-400'
                  }`}
                >
                  {message.priority}
                </Badge>
              )}
              
              {message.isPinned && (
                <Badge variant="outline" className="text-xs border-amber-500 text-amber-400">
                  Pinned
                </Badge>
              )}
            </div>
            
            <div className="text-slate-200 text-sm leading-relaxed mb-3">
              <MessageRenderer content={message.content} />
            </div>
            
            {/* Display existing reactions */}
            {message.reactions && Array.isArray(message.reactions) && message.reactions.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-3">
                {message.reactions.map((reaction: any, index: number) => (
                  <button
                    key={index}
                    onClick={() => handleReaction(message.id, reaction.emoji)}
                    className="flex items-center gap-1 px-2 py-1 bg-slate-700/50 hover:bg-slate-600/50 border border-slate-600 rounded-full text-xs transition-colors"
                  >
                    <span>{reaction.emoji}</span>
                    <span className="text-slate-400">{reaction.count}</span>
                  </button>
                ))}
              </div>
            )}
            
            <div className="flex items-center gap-2 text-xs">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleReaction(message.id, '❤️')}
                className="h-6 px-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10"
              >
                <Heart className="w-3 h-3 mr-1" />
                React
              </Button>
              
              <Button
                size="sm"
                variant="ghost"
                onClick={() => toggleReply(message.id)}
                className="h-6 px-2 text-slate-400 hover:text-blue-400 hover:bg-blue-500/10"
              >
                <Reply className="w-3 h-3 mr-1" />
                Reply
              </Button>
              
              {/* Admin/Event Manager Action Buttons */}
              {(userProfile?.role === 'admin' || userProfile?.role === 'event_manager') && (
                <>
                  {!message.isAnswered && (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleMessageAction(message.id, 'done')}
                        disabled={updateMessageStatusMutation.isPending}
                        className="h-6 px-2 text-slate-400 hover:text-green-400 hover:bg-green-500/10"
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />
                        {updateMessageStatusMutation.isPending ? 'Updating...' : 'Done'}
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleMessageAction(message.id, 'following_up')}
                        disabled={updateMessageStatusMutation.isPending}
                        className="h-6 px-2 text-slate-400 hover:text-yellow-400 hover:bg-yellow-500/10"
                      >
                        <Clock className="w-3 h-3 mr-1" />
                        {updateMessageStatusMutation.isPending ? 'Updating...' : 'Following up'}
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleMessageAction(message.id, 'in_progress')}
                        disabled={updateMessageStatusMutation.isPending}
                        className="h-6 px-2 text-slate-400 hover:text-orange-400 hover:bg-orange-500/10"
                      >
                        <ArrowRight className="w-3 h-3 mr-1" />
                        {updateMessageStatusMutation.isPending ? 'Updating...' : 'In progress'}
                      </Button>
                    </>
                  )}
                  
                  {/* Convert to Task button - only visible for admin/event managers */}
                  {userProfile && (userProfile.role === 'admin' || userProfile.role === 'event_manager') && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleConvertToTask(message)}
                      className="h-6 px-2 text-slate-400 hover:text-purple-400 hover:bg-purple-500/10"
                    >
                      <ClipboardList className="w-3 h-3 mr-1" />
                      Convert to Task
                    </Button>
                  )}
                </>
              )}
            </div>
            
            {messageReplies.length > 0 && (
              <div className="mt-3 border-l-2 border-slate-600 pl-4 space-y-2">
                {messageReplies.map(reply => (
                  <div key={reply.id} className="bg-slate-700/50 rounded p-2">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-white">{reply.authorName}</span>
                      <span className="text-xs text-slate-400">{reply.authorRole}</span>
                      <span className="text-xs text-slate-500">
                        {format(new Date(reply.createdAt), 'MMM d, h:mm a')}
                      </span>
                    </div>
                    <div className="text-sm text-slate-200">
                      <MessageRenderer content={reply.content} />
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {replyMode[message.id] && (
              <div className="mt-3 bg-slate-700/30 rounded-lg p-3 border border-slate-600">
                <textarea
                  value={replyContent[message.id] || ''}
                  onChange={(e) => handleReplyContentChange(message.id, e.target.value)}
                  onKeyPress={(e) => handleReplyKeyPress(message.id, e)}
                  placeholder="Type your reply..."
                  className="w-full h-20 px-3 py-2 bg-slate-700 text-white border border-slate-600 rounded-md focus:border-amber-500 focus:outline-none resize-none"
                />
                <div className="flex justify-end gap-2 mt-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => toggleReply(message.id)}
                    className="text-slate-400 hover:text-slate-200"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleSendReply(message.id, message)}
                    disabled={!replyContent[message.id]?.trim() || sendReplyMutation.isPending}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    {sendReplyMutation.isPending ? 'Sending...' : 'Send Reply'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Show loading state while profile is loading
  if (profileLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-900">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row h-screen bg-slate-900">
      {/* Sidebar - Hidden on mobile for clean chat experience, Hidden when in task view for support desk experience */}
      {activeView !== 'tasks' && (
        <div className="hidden lg:flex w-full lg:w-80 bg-slate-800 border-b lg:border-r lg:border-b-0 border-slate-700 flex-col">
        {/* Header */}
        <div className="p-4 border-b border-slate-700">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-6 h-6 text-amber-500" />
            <h1 className="text-xl font-bold text-white">Comm-Hub</h1>
            {/* Debug info — staff only; never shown to client-portal roles (portal audit 2026-09-24) */}
            {!['client', 'collaborator', 'planner'].includes(userProfile?.role || '') && (
              <div className="text-xs text-slate-400">
                Role: {userProfile?.role || 'loading...'}
              </div>
            )}
          </div>
          
          {/* View Toggle - Role-based visibility */}
          <div className="flex gap-1 bg-slate-700 rounded-lg p-1">
            {/* Show Inbox tab for admin and event_manager */}
            {(userProfile?.role === 'admin' || userProfile?.role === 'event_manager') ? (
              <button
                onClick={() => setActiveView('inbox')}
                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeView === 'inbox'
                    ? 'bg-amber-600 text-white'
                    : 'text-slate-300 hover:text-white hover:bg-slate-600'
                }`}
              >
                Inbox
              </button>
            ) : null}
            <button
              onClick={() => setActiveView('teams')}
              className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activeView === 'teams'
                  ? 'bg-amber-600 text-white'
                  : 'text-slate-300 hover:text-white hover:bg-slate-600'
              }`}
            >
              Teams
            </button>
            {/* Show Tasks/Tickets tab based on user role */}
            {(userProfile?.role === 'admin' || userProfile?.role === 'event_manager') ? (
              <button
                onClick={() => setActiveView('tasks')}
                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeView === 'tasks'
                    ? 'bg-amber-600 text-white'
                    : 'text-slate-300 hover:text-white hover:bg-slate-600'
                }`}
              >
                Tasks
              </button>
            ) : userProfile?.role === 'client' ? (
              <button
                onClick={() => setActiveView('tasks')}
                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeView === 'tasks'
                    ? 'bg-amber-600 text-white'
                    : 'text-slate-300 hover:text-white hover:bg-slate-600'
                }`}
              >
                Tickets
              </button>
            ) : null}
          </div>
        </div>

        {/* Team Selection - Hidden when Inbox or Tasks is active */}
        {activeView === 'teams' && (
          <div className="p-3 border-b border-slate-700">
            <select
              value={selectedTeam || ''}
              onChange={(e) => setSelectedTeam(e.target.value ? Number(e.target.value) : null)}
              className="w-full h-10 px-3 py-2 bg-slate-700 text-white border border-slate-600 rounded-md focus:border-amber-500 focus:outline-none"
            >
              <option value="">Select Team</option>
              {teams.map((team: Team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Filter Section for Inbox - Only for authorized users */}
        {activeView === 'inbox' && userProfile && (userProfile.role === 'admin' || userProfile.role === 'event_manager') && (
          <div className="p-3 border-b border-slate-700">
            <div className="space-y-2">
              {[
                { id: 'unanswered', label: 'Unanswered', count: filterCounts.unanswered },
                { id: 'all', label: 'All Messages', count: filterCounts.all },
                { id: 'priority', label: 'High Priority', count: filterCounts.priority },
                { id: 'assigned', label: 'Assigned to Me', count: filterCounts.assigned },
                { id: 'following_up', label: 'Following up', count: filterCounts.following_up },
                { id: 'in_progress', label: 'In progress', count: filterCounts.in_progress },
                { id: 'closed', label: 'Closed', count: filterCounts.closed },
              ].map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => setCurrentFilter(filter.id as any)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                    currentFilter === filter.id
                      ? 'bg-amber-600 text-white'
                      : 'text-slate-300 hover:text-white hover:bg-slate-700'
                  }`}
                >
                  <span>{filter.label}</span>
                  <Badge variant="outline" className="ml-2 border-slate-500 text-slate-300">
                    {filter.count}
                  </Badge>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Channel List for Teams View */}
        {activeView === 'teams' && selectedTeam && (
          <div className="flex-1 overflow-y-auto">
            <div className="p-3 border-b border-slate-700">
              <h3 className="text-sm font-semibold text-slate-300 mb-3">Channels</h3>
              {channelsLoading ? (
                <div className="text-slate-400 text-sm">Loading channels...</div>
              ) : channelsError ? (
                <div className="text-red-400 text-sm">Error loading channels</div>
              ) : selectedTeamChannels.length > 0 ? (
                <div className="space-y-1">
                  {selectedTeamChannels.map((channel: Channel) => (
                    <button
                      key={channel.id}
                      onClick={() => setSelectedChannel(channel.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                        selectedChannel === channel.id
                          ? 'bg-amber-600 text-white'
                          : 'text-slate-300 hover:text-white hover:bg-slate-700'
                      }`}
                    >
                      <Hash className="w-4 h-4" />
                      <span>{channel.name}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-slate-400 text-sm">No channels available</div>
              )}
            </div>
          </div>
        )}

        </div>
      )}

      {/* Mobile Header - Only visible on mobile when sidebar is hidden */}
      <div className="lg:hidden bg-slate-800 border-b border-slate-700 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Hash className="w-5 h-5 text-amber-500" />
            <span className="font-semibold text-white text-sm">
              {selectedChannel ? 
                channels.find(c => c.id === selectedChannel)?.name || 'Channel' : 
                'Communication Hub'
              }
            </span>
            {selectedTeam && (
              <span className="text-slate-400 text-xs">
                in {teams.find(t => t.id === selectedTeam)?.name}
              </span>
            )}
          </div>
          
          {/* Mobile Navigation Tabs - Only show for clients with teams view */}
          {userProfile?.role === 'client' && activeView === 'teams' && (
            <div className="flex gap-1">
              <button
                onClick={() => setActiveView('teams')}
                className={`px-2 py-1 text-xs rounded ${
                  activeView === 'teams' ? 'bg-amber-600 text-white' : 'text-slate-400'
                }`}
              >
                Chat
              </button>
              <button
                onClick={() => setActiveView('tasks')}
                className={`px-2 py-1 text-xs rounded ${
                  activeView === 'tasks' ? 'bg-amber-600 text-white' : 'text-slate-400'
                }`}
              >
                Support
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-0 lg:min-h-screen">
        {/* Main Content Conditional Rendering */}
        {(() => {
          // PRIORITY 1: Tasks view - must be checked FIRST
          if (activeView === 'tasks' && userProfile) {
            return (
              <TaskManagementView userProfile={userProfile} onViewChange={setActiveView} />
            );
          }
          
          // PRIORITY 2: Inbox view - for admin/event_manager only
          if (activeView === 'inbox' && userProfile && (userProfile.role === 'admin' || userProfile.role === 'event_manager') && filteredInboxMessages.length > 0) {
            return (
              <div className="flex-1 flex flex-col">
                <div className="p-4 border-b border-slate-700 bg-slate-800">
                  <h2 className="text-xl font-semibold text-white">
                    Inbox - {currentFilter.charAt(0).toUpperCase() + currentFilter.slice(1)} ({filteredInboxMessages.length})
                  </h2>
                </div>
                
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {filteredInboxMessages.map((message: Message) => (
                      <div key={message.id} className="flex items-start gap-3 p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
                        <Avatar className="w-8 h-8">
                          <AvatarFallback className="bg-amber-500/20 text-amber-300">
                            {message.authorName?.charAt(0) || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-white">{message.authorName}</span>
                            <span className="text-xs text-slate-400">
                              {new Date(message.createdAt).toLocaleString()}
                            </span>
                            <div className="flex items-center gap-1">
                              {message.teamName && (
                                <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
                                  {message.teamName}
                                </Badge>
                              )}
                              {message.channelName && (
                                <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
                                  #{message.channelName}
                                </Badge>
                              )}
                              {message.priority && message.priority !== 'normal' && (
                                <Badge 
                                  variant="outline" 
                                  className={`text-xs ${
                                    message.priority === 'high' || message.priority === 'urgent'
                                      ? 'border-red-500 text-red-400' 
                                      : 'border-yellow-500 text-yellow-400'
                                  }`}
                                >
                                  {message.priority}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <p className="text-slate-300 text-sm break-words">{message.content}</p>
                          
                          {/* Status and Action Buttons */}
                          <div className="flex items-center gap-2 mt-3">
                            <button 
                              onClick={() => updateMessageStatusMutation.mutate({ messageId: message.id, status: 'closed' })}
                              className="px-3 py-1 text-xs bg-green-600/20 text-green-300 rounded-md hover:bg-green-600/30 transition-colors"
                            >
                              Done
                            </button>
                            <button 
                              onClick={() => updateMessageStatusMutation.mutate({ messageId: message.id, status: 'following_up' })}
                              className="px-3 py-1 text-xs bg-blue-600/20 text-blue-300 rounded-md hover:bg-blue-600/30 transition-colors"
                            >
                              Following up
                            </button>
                            <button 
                              onClick={() => updateMessageStatusMutation.mutate({ messageId: message.id, status: 'in_progress' })}
                              className="px-3 py-1 text-xs bg-yellow-600/20 text-yellow-300 rounded-md hover:bg-yellow-600/30 transition-colors"
                            >
                              In progress
                            </button>
                            {userProfile && (userProfile.role === 'admin' || userProfile.role === 'event_manager') && (
                              <button 
                                onClick={() => handleConvertToTask(message)}
                                className="px-3 py-1 text-xs bg-purple-600/20 text-purple-300 rounded-md hover:bg-purple-600/30 transition-colors"
                              >
                                Convert to Task
                              </button>
                            )}
                            <button 
                              onClick={() => handleReaction(message.id, '👍')}
                              className="px-3 py-1 text-xs bg-slate-600/50 text-slate-300 rounded-md hover:bg-slate-600/70 transition-colors"
                            >
                              React
                            </button>
                            <button 
                              onClick={() => toggleReply(message.id)}
                              className="px-3 py-1 text-xs bg-slate-600/50 text-slate-300 rounded-md hover:bg-slate-600/70 transition-colors"
                            >
                              Reply
                            </button>
                          </div>

                          {/* Reply Section */}
                          {replyMode[message.id] && (
                            <div className="mt-3 pt-3 border-t border-slate-700">
                              <textarea
                                value={replyContent[message.id] || ''}
                                onChange={(e) => setReplyContent(prev => ({ ...prev, [message.id]: e.target.value }))}
                                onKeyPress={(e) => handleReplyKeyPress(message.id, e)}
                                placeholder="Type your reply..."
                                className="w-full px-3 py-2 bg-slate-700 text-white border border-slate-600 rounded-md focus:border-amber-500 focus:outline-none resize-none"
                                rows={3}
                              />
                              <div className="flex items-center gap-2 mt-2">
                                <button
                                  onClick={() => handleSendReply(message.id, message)}
                                  className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors text-sm"
                                >
                                  Send Reply
                                </button>
                                <button
                                  onClick={() => toggleReply(message.id)}
                                  className="px-4 py-2 bg-slate-600 text-white rounded-md hover:bg-slate-700 transition-colors text-sm"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            );
          } else if (selectedChannel) {
            return (
          <div className="flex-1 flex flex-col h-full">
            {/* Channel Header */}
            <div className="flex-shrink-0 p-4 border-b border-slate-700 bg-slate-800">
              <div className="flex items-center gap-2">
                <Hash className="w-5 h-5 text-slate-400" />
                <span className="font-semibold text-white">
                  {channels.find(c => c.id === selectedChannel)?.name}
                </span>
                <span className="text-slate-400">
                  in {teams.find(t => t.id === selectedTeam)?.name}
                </span>
              </div>
            </div>
            
            {/* Messages Area - Takes remaining space */}
            <div className="flex-1 overflow-hidden">
              <ScrollArea className="h-full p-4">
                <div className="space-y-4 pb-4">
                  {messages
                      .filter(msg => msg.channelId === selectedChannel)
                      .map((message: Message) => (
                      <div key={message.id} className="flex items-start gap-3">
                        <Avatar className="w-8 h-8">
                          <AvatarFallback className="bg-amber-500/20 text-amber-300">
                            {message.authorName?.charAt(0) || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-white">{message.authorName}</span>
                            <span className="text-xs text-slate-400">
                              {format(new Date(message.createdAt), 'MMM d, h:mm a')}
                            </span>
                          </div>
                          <div className="text-slate-200">
                            <MessageRenderer content={message.content} />
                            
                            {/* Action buttons for admins/event managers */}
                            {userProfile && (userProfile.role === 'admin' || userProfile.role === 'event_manager') && (
                              <div className="flex items-center gap-2 mt-2">
                                <button 
                                  onClick={() => handleConvertToTask(message)}
                                  className="px-3 py-1 text-xs bg-purple-600/20 text-purple-300 rounded-md hover:bg-purple-600/30 transition-colors"
                                  title="Convert message to task"
                                >
                                  Convert to Task
                                </button>
                                <button 
                                  onClick={() => handleReaction(message.id, '👍')}
                                  className="px-3 py-1 text-xs bg-slate-600/50 text-slate-300 rounded-md hover:bg-slate-600/70 transition-colors"
                                  title="React to message"
                                >
                                  React
                                </button>
                              </div>
                            )}

                            {/* Render attachments if they exist */}
                            {message.attachments && message.attachments.length > 0 && (
                              <div className="mt-2 space-y-2">
                                {console.log('📎 Rendering attachments for message:', message.id, message.attachments)}
                                {message.attachments.map((attachment: any, index: number) => (
                                  <div key={index} className="max-w-md">
                                    {attachment.url && attachment.url.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                      // Image attachment
                                      <div className="relative group">
                                        <img 
                                          src={attachment.url} 
                                          alt="Uploaded image"
                                          className="rounded-lg border border-slate-600 max-w-full h-auto cursor-pointer hover:opacity-90 transition-opacity"
                                          onClick={() => window.open(attachment.url, '_blank')}
                                        />
                                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                          <button
                                            onClick={() => window.open(attachment.url, '_blank')}
                                            className="p-1 bg-black/50 rounded-full text-white hover:bg-black/70"
                                            title="Open in new tab"
                                          >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                            </svg>
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      // File attachment
                                      <a 
                                        href={attachment.url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 p-3 bg-slate-700/50 border border-slate-600 rounded-lg hover:bg-slate-700 transition-colors"
                                      >
                                        <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <span className="text-sm text-slate-300">File attachment</span>
                                        <svg className="w-4 h-4 text-slate-400 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                      </a>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Display existing reactions */}
                            {message.reactions && Array.isArray(message.reactions) && message.reactions.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {message.reactions.map((reaction: any, index: number) => (
                                  <button
                                    key={index}
                                    onClick={() => handleReaction(message.id, reaction.emoji)}
                                    className="flex items-center gap-1 px-2 py-1 bg-slate-700/50 hover:bg-slate-600/50 border border-slate-600 rounded-full text-xs transition-colors"
                                  >
                                    <span>{reaction.emoji}</span>
                                    <span className="text-slate-400">{reaction.count}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </ScrollArea>
            </div>
            
            {/* Input Area - Fixed at bottom */}
            <div className="flex-shrink-0 p-4 border-t border-slate-700 bg-slate-900/50">
              {(() => {
                const currentChannel = channels.find(c => c.id === selectedChannel);
                // Check if this is an announcements channel (IDs 26, 27, 28, 29, 30)
                const announcementChannelIds = [26, 27, 28, 29, 30];
                const isAnnouncementsChannel = announcementChannelIds.includes(selectedChannel || 0) || 
                                             currentChannel?.name?.toLowerCase() === 'announcements';
                const isClientUser = userProfile?.role === 'client';
                const isReadOnly = isAnnouncementsChannel && isClientUser;
                
                if (isReadOnly) {
                  return (
                    <div className="flex items-center gap-3 p-3 bg-amber-900/20 border border-amber-700/50 rounded-lg">
                      <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                      <p className="text-amber-200 text-sm">
                        <span className="font-medium">#{currentChannel?.name}</span> is read-only. Only administrators can post announcements.
                      </p>
                    </div>
                  );
                }
                
                return (
                  <SlackStyleInput
                    onSend={handleSendMessage}
                    placeholder={`Message #${currentChannel?.name || 'channel'}`}
                    disabled={sendMessageMutation.isPending}
                  />
                );
              })()}
            </div>
          </div>
            );
          } else if (activeView === 'teams') {
            return (
          <div className="flex-1 flex flex-col">
            {selectedChannel && messages.length > 0 ? (
              <>
                {/* Channel header */}
                <div className="p-4 border-b border-slate-700 bg-slate-800">
                  <div className="flex items-center gap-2">
                    <Hash className="w-5 h-5 text-amber-500" />
                    <h2 className="font-semibold text-white">
                      {channels.find(c => c.id === selectedChannel)?.name || 'Unknown Channel'}
                    </h2>
                  </div>
                </div>
                
                {/* Messages */}
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {messages.map((message: Message) => (
                      <div key={message.id} className="flex items-start gap-3">
                        <Avatar className="w-8 h-8">
                          <AvatarFallback className="bg-slate-600 text-white text-xs">
                            {message.authorName ? message.authorName.charAt(0).toUpperCase() : 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 mb-1">
                            <span className="font-medium text-white">{message.authorName || 'Unknown User'}</span>
                            <span className="text-xs text-slate-400">
                              {format(new Date(message.createdAt), 'h:mm a')}
                            </span>
                          </div>
                          <MessageRenderer content={message.content} />
                          {message.attachments && message.attachments.length > 0 && (
                            <div className="mt-2 space-y-2">
                              {message.attachments.map((attachment: FileAttachment, index: number) => (
                                <div key={index} className="flex items-center gap-2 p-2 bg-slate-800 rounded border border-slate-700">
                                  <span className="text-sm text-slate-300">{attachment.fileName}</span>
                                  <a 
                                    href={attachment.wasabiUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-2 py-1 rounded"
                                  >
                                    Download
                                  </a>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                
                {/* Input area */}
                <div className="flex-shrink-0 p-4 border-t border-slate-700 bg-slate-900/50">
                  {(() => {
                    const currentChannel = channels.find(c => c.id === selectedChannel);
                    const announcementChannelIds = [26, 27, 28, 29, 30];
                    const isAnnouncementsChannel = announcementChannelIds.includes(selectedChannel || 0) || 
                                                 currentChannel?.name?.toLowerCase() === 'announcements';
                    const isClientUser = userProfile?.role === 'client';
                    const isReadOnly = isAnnouncementsChannel && isClientUser;
                    
                    if (isReadOnly) {
                      return (
                        <div className="flex items-center gap-3 p-3 bg-amber-900/20 border border-amber-700/50 rounded-lg">
                          <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                          <p className="text-amber-200 text-sm">
                            <span className="font-medium">#{currentChannel?.name}</span> is read-only. Only administrators can post announcements.
                          </p>
                        </div>
                      );
                    }
                    
                    return (
                      <SlackStyleInput
                        onSend={handleSendMessage}
                        placeholder={`Message #${currentChannel?.name || 'channel'}`}
                        disabled={sendMessageMutation.isPending}
                      />
                    );
                  })()}
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center text-slate-500">
                  <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <h3 className="text-xl font-semibold mb-2">Select a channel</h3>
                  <p>Choose a channel from the sidebar to start communicating</p>
                </div>
              </div>
            )}
          </div>
            );
          } else {
            return (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center text-slate-500">
                  <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <h3 className="text-xl font-semibold mb-2">Welcome to Comm-Hub</h3>
                  <p>Select a channel to start communicating with your team</p>
                </div>
              </div>
            );
          }
        })()}
      </div>
    </div>
  );
}